import React, { useCallback, useMemo } from 'react'
import { useAtom } from 'jotai'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import ToggleIssueTypes from '../components/ToggleIssueTypes'
import { newsFiltersAtom, NewsFilterType } from '../../atoms/issueAtom'
import DropdownSelect from '../components/DropdownSelect'
import ToggleWatchLists from '../components/ToggleWatchLists'
import { useTranslation } from 'react-i18next'

const sourceTypeItems = [
    { id: 'cancellation', labelKey: 'entities.news.eventType.cancellation' },
    { id: 'new commitments', labelKey: 'entities.news.eventType.newCommitments' },
    { id: 'Guarantor fee issue', labelKey: 'entities.news.eventType.guarantorCompensation' },
    { id: 'publication', labelKey: 'entities.news.eventType.publication' },
    { id: 'prospectus', labelKey: 'entities.news.eventType.prospectus' },
    { id: 'outcome', labelKey: 'entities.news.eventType.outcome' },
    { id: 'terms', labelKey: 'entities.news.eventType.terms' },
    { id: 'conversion', labelKey: 'entities.news.eventType.conversion' },
] as const

type HandleChangeTypes = NewsFilterType

function handleChange<T extends keyof HandleChangeTypes>(
    type: T,
    setFilters: (cb: (v: HandleChangeTypes) => HandleChangeTypes) => void,
): (v: HandleChangeTypes[T]['value']) => void
function handleChange(
    type: 'sourceType',
    setFilters: (cb: (v: HandleChangeTypes) => HandleChangeTypes) => void,
): (v: string[]) => void {
    return (value: string[]) => {
        let newValue = Array.isArray(value) ? [...value] : value

        if (type === 'sourceType') {
            if (value.includes('prospectus')) {
                newValue.push('memorandum')
            } else {
                newValue = newValue.filter((v) => v !== 'memorandum')
            }
        }

        setFilters((filters: HandleChangeTypes) => ({ ...filters, [type]: { value: newValue } }))
    }
}

export const NewsFilter = () => {
    const { t } = useTranslation()
    const [filters, setFilters] = useAtom(newsFiltersAtom)
    const handleChangeIssueType = useCallback(
        (value: NewsFilterType['issueTypes']['value']) => handleChange('issueTypes', setFilters)(value),
        [setFilters],
    )
    const handleChangeSourceType = useCallback(
        (value: string[]) => handleChange('sourceType', setFilters)(value),
        [setFilters],
    )
    const handleChangeWatchLists = useCallback(
        (value: boolean) => handleChange('onlyInWatchLists', setFilters)(value),
        [setFilters],
    )

    const sortedSourceTypes = useMemo(
        () =>
            [...sourceTypeItems]
                .map((item) => ({ id: item.id, label: t(item.labelKey) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [t],
    )

    return (
        <Grid container spacing={6} sx={{ mb: 2, pb: 4 }}>
            <Grid>
                <ToggleIssueTypes dense value={filters.issueTypes.value} onChange={handleChangeIssueType} />
            </Grid>
            <Grid
                size={{ sm: 3, md: 2, lg: 1 }}
                sx={{
                    minWidth: 250,
                }}
            >
                <Box sx={{ paddingBottom: 4 }}>
                    <DropdownSelect
                        label={t('entities.news.fields.newsType')}
                        items={sortedSourceTypes}
                        multiple
                        selectedItems={filters.sourceType.value}
                        onChange={handleChangeSourceType}
                    />
                </Box>
            </Grid>
            <Grid>
                <ToggleWatchLists onChange={handleChangeWatchLists} value={filters.onlyInWatchLists.value} />
            </Grid>
        </Grid>
    )
}
